/**
 * Scheduled Events Service
 *
 * Wraps the unified `/scheduled-events` feed that powers both the
 * Calendar and Map views. Work Orders and Inspections come back in a
 * single normalized event shape so the UI doesn't have to merge two
 * different record types itself.
 *
 * The endpoint is server-side role-scoped:
 *   - super_admin / developer            : all events platform-wide
 *   - admin / manager / company_admin    : events for the user's companies
 *   - technician / reviewer              : only events the user is on
 *
 * Filters are passed as query parameters; multi-value filters use
 * PHP-style array bracket syntax (e.g. `assignee_ids[]=1&assignee_ids[]=2`)
 * which Laravel parses into an array automatically.
 */

import { api, camelizeKeys } from './api';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** A user appearing on an event (assignee, inspector, etc.). */
export interface ScheduledEventPerson {
    id: string;
    name: string;
}

/**
 * The unified event shape returned by GET /scheduled-events.
 * Both Work Orders and Inspections are projected into this shape on
 * the server. The `recordType` field lets the UI render type-specific
 * affordances (icons, badges, links into the correct detail page).
 *
 * Date fields:
 *   - start / end : ISO 8601 strings. When start_time is null on the
 *                   source record the server returns the date alone
 *                   ("YYYY-MM-DD") and sets allDay=true.
 *   - scheduledDate : the bare date for grouping in calendar headers.
 */
/**
 * Wire-level record type, mirrored from the backend payload. The
 * derived "project work order" classification used by the calendar
 * (see `getEventCategory`) is a UI concern and is *not* on the wire.
 */
export type ScheduledEventRecordType = 'work_order' | 'inspection';

export interface ScheduledEvent {
    id: string;                               // composite (e.g. "wo-123")
    recordId: string;                         // numeric source-record id
    recordType: ScheduledEventRecordType;
    title: string;
    status: string;
    start: string;
    end: string;
    allDay: boolean;
    scheduledDate: string;
    /** Only set on work orders. Null on inspections. */
    businessLine: string | null;
    /** Only set on work orders. Null on inspections. */
    projectName: string | null;
    /** Parent Project Work Order id — set when a work order belongs to a
     *  project grouping. Null on inspections and standalone WOs. */
    projectWorkOrderId: string | null;
    /** Parent Project Work Order display name (see projectWorkOrderId). */
    projectWorkOrderName: string | null;
    companyId: string | null;
    companyName: string | null;
    locationId: string | null;
    locationName: string | null;
    address: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    assignees: ScheduledEventPerson[];
    detailUrl: string;
}

/**
 * Filter parameters accepted by GET /scheduled-events. Every field is
 * optional; pass any combination to narrow the feed.
 */
export interface ScheduledEventFilters {
    /** ISO date (YYYY-MM-DD) — inclusive lower bound on scheduled date */
    from?: string;
    /** ISO date (YYYY-MM-DD) — inclusive upper bound on scheduled date */
    to?: string;
    /** User ids — match against WO assignees + Inspection inspector/assignee */
    assigneeIds?: number[];
    /** Customer companies */
    companyIds?: number[];
    /** Cities (matched on the linked location) */
    cities?: string[];
    /** States (matched on the linked location) */
    states?: string[];
    /**
     * Which record types to include. Omit to get both — the backend
     * defaults to both for backwards compatibility.
     */
    recordTypes?: ScheduledEventRecordType[];
}

/* ============================================
 * SERVICE
 * ============================================ */

/**
 * Convert the camelCased filter object into the snake_case + bracketed
 * query string Laravel expects. Multi-value arrays use `key[]=value`
 * repeated for each element.
 *
 * Returned as a plain object compatible with api.get's params arg.
 */
function buildQueryParams(filters: ScheduledEventFilters): Record<string, string> {
    const params: Record<string, string> = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;

    // For array params we encode multiple values via `key[]=v1&key[]=v2`.
    // api.get's params helper joins as `key=value`, so we pre-join here
    // and let URLSearchParams handle bracketed array notation downstream.
    const arrayMappings: Array<[keyof ScheduledEventFilters, string]> = [
        ['assigneeIds', 'assignee_ids'],
        ['companyIds', 'company_ids'],
        ['cities', 'cities'],
        ['states', 'states'],
        ['recordTypes', 'record_types'],
    ];
    for (const [camelKey, snakeKey] of arrayMappings) {
        const values = filters[camelKey] as (string | number)[] | undefined;
        if (values && values.length) {
            // Pass each value with the bracketed key. api.ts builds the
            // query string via URLSearchParams which preserves repeated keys.
            values.forEach((value, idx) => {
                params[`${snakeKey}[${idx}]`] = String(value);
            });
        }
    }
    return params;
}

/**
 * UI-level event category. Derived from the wire payload — the backend
 * doesn't model "project work orders" as a distinct entity; we treat
 * construction-business-line work orders as project work orders for
 * visual + grouping purposes on the calendar.
 *
 * Keep this in sync with the color map in CalendarView and the filter
 * options exposed in the EventFiltersBar.
 */
export type ScheduledEventCategory =
    | 'inspection'
    | 'project_work_order'
    | 'work_order';

/**
 * Single source of truth for the "is this a project work order?" rule.
 * Exposed separately so the WO detail page (which holds a WorkOrder,
 * not a ScheduledEvent) can derive the same category without faking
 * the event shape.
 */
export function isProjectBusinessLine(businessLine: string | null | undefined): boolean {
    return businessLine === 'construction';
}

/**
 * Project the wire event into its UI category. Inspections short-circuit;
 * work orders are split on business_line so the construction subset
 * renders with the "project" treatment.
 */
export function getEventCategory(event: ScheduledEvent): ScheduledEventCategory {
    if (event.recordType === 'inspection') return 'inspection';
    if (isProjectBusinessLine(event.businessLine)) return 'project_work_order';
    return 'work_order';
}

export const scheduledEventsService = {
    /**
     * GET /scheduled-events — fetch the role-scoped feed with optional
     * filters. Camelizes the snake_case wire payload here so consumers
     * can read `event.detailUrl` etc. directly. The base api client
     * does not camelize responses; each service is responsible for it.
     */
    list: async (filters: ScheduledEventFilters = {}) => {
        const response = await api.get<{ data: ScheduledEvent[] }>(
            '/scheduled-events',
            buildQueryParams(filters)
        );
        return {
            ...response,
            data: camelizeKeys(response.data) as { data: ScheduledEvent[] },
        };
    },
};
